'use client';
import { CARD_TEST_IDS, FABRIC_PANEL_TEST_IDS, ON_BOARDING_TIP_ITEM_TEST_IDS } from '@susu/custom-made/utils/testIds';
import cn from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import Tooltip from '../../tooltip/tooltip';
import './onBoardingTipItem.css';
import { useI18N } from '@susu/i18n/client';

/**
 * Each tip is a copy of a real icon (the card's info icon, the toolbar's filter icon) drawn on top
 * of the dimming overlay. The copies keep the size of the icon they mimic and are centred on it,
 * so the only thing to work out is where that icon currently is: the side panel is a percentage of
 * the viewport, so there is no fixed offset left to pin them to.
 */
const CENTRED_ON_TARGET = 'fixed -translate-x-1/2 -translate-y-1/2';

const INFO_ICON_CONTAINER_CLASSES = `${CENTRED_ON_TARGET} flex w-20 h-20 lg:w-24 lg:h-24 rounded-full bg-white justify-center items-center text-gray-800 text-[13px] lg:text-[15px] [&>i]:text-gray-800 [&>i]:font-bold`;

const FILTER_CONTAINER_CLASSES = `${CENTRED_ON_TARGET} flex z-1 cursor-pointer p-6 justify-center items-center bg-white rounded-[8px]`;

/** A selected card's icon can still be mid-paint when the tip fires; give it a frame-by-frame budget before giving up. */
const INFO_ICON_RETRY_TIMEOUT_MS = 1000;

/** Only the selected card renders an info icon; the others keep theirs at `display: none`. */
const findInfoIcon = () =>
  Array.from(document.querySelectorAll<HTMLElement>(`[data-testid="${CARD_TEST_IDS.INFO_BTN}"]`)).find(
    icon => icon.offsetParent,
  );

const findFilterIcon = () =>
  document.querySelector(`[data-testid="${FABRIC_PANEL_TEST_IDS.FILTER_BTN}"] .cm-fabric-filter-icon`);

export const OnBoardingTipItem = ({
  isShown,
  hideTooltip,
  saveStorage,
}: {
  isShown: boolean;
  hideTooltip: () => void;
  saveStorage: () => void;
}) => {
  const { t } = useI18N();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipIndex, setTooltipIndex] = useState<number>(0);
  const [infoRect, setInfoRect] = useState<DOMRect | null>(null);
  const [filterRect, setFilterRect] = useState<DOMRect | null>(null);

  /** Opens the tooltip above `target` and returns its box, which also places the copied icon. */
  const openTooltip = (target: Element | null | undefined, description: string) => {
    const elementRect = target?.getBoundingClientRect() ?? null;

    if (elementRect) {
      tooltipRef.current?.dispatchEvent(
        new CustomEvent('tooltipOpened', {
          detail: {
            tooltipId: 'onboardingTips',
            title: null,
            description,
            topPosition: elementRect.top,
            leftPosition: elementRect.left,
            width: elementRect.width,
          },
          bubbles: true,
        }),
      );
    }

    return elementRect;
  };

  const showInfoTooltip = () => {
    const elementRect = openTooltip(findInfoIcon(), t('onBoarding.tipFabricInfo'));
    setInfoRect(elementRect);
    return !!elementRect;
  };

  const showFilterTooltip = () => {
    const elementRect = openTooltip(findFilterIcon(), t('onBoarding.tipFilterFabrics'));
    setFilterRect(elementRect);
    return !!elementRect;
  };

  const finish = () => {
    hideTooltip();
    saveStorage();
  };

  const goToFilterTooltip = () => {
    setTooltipIndex(0);
    setTimeout(() => {
      setTooltipIndex(2);
      if (!showFilterTooltip()) {
        finish();
      }
    }, 500);
  };

  const closeCallbackFC = () => {
    if (tooltipIndex === 1) {
      goToFilterTooltip();
    } else if (tooltipIndex === 2) {
      finish();
    }
  };

  const adjustTooltip = useCallback(() => {
    if (tooltipIndex === 1) {
      showInfoTooltip();
    } else if (tooltipIndex === 2) {
      showFilterTooltip();
    }
  }, [tooltipIndex]);

  useEffect(() => {
    window.addEventListener('resize', adjustTooltip);

    return () => {
      window.removeEventListener('resize', adjustTooltip);
    };
  }, [adjustTooltip]);

  useEffect(() => {
    if (!isShown) {
      return;
    }

    setTooltipIndex(1);

    let cancelled = false;
    const retryDeadline = Date.now() + INFO_ICON_RETRY_TIMEOUT_MS;

    const tryShowInfoTooltip = () => {
      if (cancelled || showInfoTooltip()) {
        return;
      }
      if (Date.now() < retryDeadline) {
        // Selected card exists but its icon hasn't painted yet — retry next frame instead of skipping the step.
        requestAnimationFrame(tryShowInfoTooltip);
      } else {
        // Still nothing to point at (no card selected) — go straight to the filter tip.
        goToFilterTooltip();
      }
    };

    tryShowInfoTooltip();

    return () => {
      cancelled = true;
    };
  }, [isShown]);

  return (
    <>
      {isShown && (
        <div ref={tooltipRef} className="fixed inset-0 bg-gray-800/30 z-12">
          <Tooltip
            tooltipId="onboardingTips"
            noResizeClosing={true}
            closeCallbackFC={closeCallbackFC}
            isSingleLineText={true}
          />
          {infoRect && (
            <div
              className={cn(INFO_ICON_CONTAINER_CLASSES, tooltipIndex === 1 ? 'visible' : 'invisible')}
              style={{ top: infoRect.top + infoRect.height / 2, left: infoRect.left + infoRect.width / 2 }}
              data-testid={ON_BOARDING_TIP_ITEM_TEST_IDS.TOOLTIP_INFO}
            >
              <i className="susu-icons" translate="no">
                info
              </i>
            </div>
          )}
          {filterRect && (
            <div
              className={cn(FILTER_CONTAINER_CLASSES, tooltipIndex === 2 ? 'visible' : 'invisible')}
              style={{ top: filterRect.top + filterRect.height / 2, left: filterRect.left + filterRect.width / 2 }}
              data-testid={ON_BOARDING_TIP_ITEM_TEST_IDS.TOOLTIP_FILTER}
            >
              <span className="cm-ob-filter-icon shrink-0 bg-center bg-no-repeat bg-contain w-20 h-20 md:w-24 md:h-24 lg:w-20 lg:h-20"></span>
            </div>
          )}
        </div>
      )}
    </>
  );
};
